<script setup lang="ts">
import { computed } from 'vue';
import { SLOTS, type Inventory, type Artifact, type EquipSlot } from '../game/inventory-api';
import { itemThumb, UI } from '../game/art';
import { RARITY_HEX, SLOT_LABELS, durabilityPct, effectiveLevel } from '../game/item-math';
import BuySlots from './BuySlots.vue';
import SwipeDeck from './SwipeDeck.vue';
import { t } from '../i18n';

/**
 * THE SEA-CHEST — the middle column of the Hero page. Purely presentational:
 * the inventory snapshot is owned by HeroScreen (single source of truth) and
 * handed down as a prop; this component only renders the filterable item grid
 * and asks the parent to open the shared ItemModal for a given item.
 *
 * NOTE: gear mutations (equip/upgrade/repair/sell) now live entirely in the
 * shared <ItemModal> (owned by HeroScreen) so one modal instance serves the
 * sea-chest grid, the hero-card equipped strip AND the dossier paperdoll. The
 * `updated` event stays declared here for interface stability even though this
 * component no longer performs mutations directly.
 */
// `filter` is a controlled prop (v-model:filter from HeroScreen) so clicking an
// empty equipped-strip slot on the hero card can drive this grid's filter too.
const props = defineProps<{ inventory: Inventory; filter: EquipSlot | 'ALL' }>();
const emit = defineEmits<{
  (e: 'inspect', artifact: Artifact, slot: EquipSlot): void;
  (e: 'update:filter', filter: EquipSlot | 'ALL'): void;
  (e: 'updated'): void;
}>();
void emit; // 'updated' kept declared for API compatibility — see NOTE above

const FILTER_TABS: { key: EquipSlot | 'ALL'; label: string }[] = [
  { key: 'ALL', get label() { return t('inventory.all'); } },
  ...SLOTS.map((s) => ({ key: s, get label() { return SLOT_LABELS[s]; } })),
];

function slotOf(a: Artifact): EquipSlot {
  return a.type.toLowerCase() as EquipSlot;
}

/** Equipped items first (so their gold "E" badge reads naturally near the top), then the sea-chest. */
const allItems = computed<Artifact[]>(() => {
  const equipped = SLOTS.map((s) => props.inventory.equipped[s]).filter((a): a is Artifact => !!a);
  return [...equipped, ...props.inventory.bag];
});

function isEquipped(a: Artifact): boolean {
  return props.inventory.equipped[slotOf(a)]?.id === a.id;
}
const equippedIds = computed(() => new Set(allItems.value.filter((a) => isEquipped(a)).map((a) => a.id)));

const filtered = computed(() =>
  props.filter === 'ALL' ? allItems.value : allItems.value.filter((a) => slotOf(a) === props.filter));

/** Bought-but-unused capacity, drawn as real dashed cells (the owner paid for
 *  those slots — they should be visible, not a number in a corner). Only on
 *  the ALL tab: capacity belongs to the whole chest, not to one gear type. */
type EmptyCell = { id: string; empty: true };
const emptyCells = computed<EmptyCell[]>(() => {
  if (props.filter !== 'ALL') return [];
  const free = Math.max(0, (props.inventory.bagCap ?? 0) - allItems.value.length);
  return Array.from({ length: free }, (_, i) => ({ id: 'empty-' + i, empty: true as const }));
});
const cells = computed<(Artifact | EmptyCell)[]>(() => [...filtered.value, ...emptyCells.value]);

function open(a: Artifact) {
  emit('inspect', a, slotOf(a));
}
</script>

<template>
  <section class="flex flex-col min-h-0 flex-1">
    <div class="flex items-center justify-between gap-3 mb-3 flex-wrap shrink-0">
      <div class="flex items-center gap-2.5">
        <h2 class="font-heading text-xl text-gold leading-none">{{ t('inventory.title') }}</h2>
        <!-- The capacity is a paid-for number — it reads like one now, not like a
             footnote (owner: "3 / 30 slots very bad visibility"). -->
        <span v-if="inventory.bagCap" class="stash-cap leading-none">
          <b class="font-heading text-base text-gold-bright tabular-nums">{{ inventory.bagCount }}</b>
          <span class="text-text-dim mx-1">/</span>
          <b class="font-heading text-base text-gold tabular-nums">{{ inventory.bagCap }}</b>
          <span class="font-label uppercase tracking-wider text-[10px] text-text-dim ml-1.5">{{ t('inventory.slots') }}</span>
        </span>
        <BuySlots @updated="emit('updated')" />
      </div>
      <nav class="flex items-center gap-3 flex-wrap">
        <button
          v-for="tab in FILTER_TABS" :key="tab.key"
          @click="emit('update:filter', tab.key)"
          :class="['chip-rune', filter === tab.key && 'chip-rune-active']"
        >{{ tab.label }}</button>
      </nav>
    </div>

    <p v-if="allItems.length === 0" class="font-body text-text-dim text-sm">{{ t('inventory.empty') }}</p>

    <!-- the sea-chest scrolls inside its panel on desktop; on mobile it becomes a
         2-column, 6-per-page swipe deck (the page never scrolls sideways) -->
    <div v-else class="stash-scroll stash-chest" :style="{ '--stash-plate': `url(${UI.chestTray})` }">
      <SwipeDeck
        :items="cells" :page-size="6"
        min-col="120px" gap="0.65rem" :min-cells="8" pad-page
        :item-key="(a: any) => a.id"
      >
        <template #default="{ item: a }">
        <div v-if="(a as any).empty" class="stash-empty-cell" aria-hidden="true"></div>
        <template v-else>
        <!-- the RELIC card — the game-wide item grammar (see .relic in app.css):
             rarity cord chased into the top edge, the thing itself floating BIG
             over its socket, name struck in the rarity's colour beneath. -->
        <button
          class="relic"
          :class="{ 'relic--legend': a.rarity === 'LEGENDARY', 'is-broken': a.isBroken }"
          :style="{ '--rar': RARITY_HEX[a.rarity] }"
          @click="open(a)"
        >
          <span class="relic-cord" aria-hidden="true"></span>
          <span v-if="equippedIds.has(a.id)" class="relic-badge">{{ t('inventory.worn') }}</span>
          <span class="relic-lv">{{ t('inventory.tier', { tier: effectiveLevel(a) }) }}</span>
          <img :src="itemThumb(a.type, a.rarity, a.name)" :alt="a.type" class="relic-art" />
          <span class="relic-name">{{ a.name }}</span>
          <span v-if="a.isBroken" class="relic-meta" style="color: #d98a8a">{{ t('inventory.broken') }}</span>
          <span v-else class="relic-meta">{{ SLOT_LABELS[slotOf(a)] }}</span>
          <span
            v-if="a.isBreakable && a.maxDurability != null"
            class="relic-wear"
            :style="{ '--wear': durabilityPct(a) > 50 ? 'var(--color-success)' : durabilityPct(a) > 25 ? '#d9a441' : 'var(--color-error)' }"
          ><b :style="{ width: durabilityPct(a) + '%' }"></b></span>
        </button>
        </template>
        </template>
      </SwipeDeck>
    </div>
  </section>
</template>

<style scoped>
/* THE CHEST. The stash is what you carry, and what you carry lives in a chest —
   so the grid sits inside one instead of floating on a flat panel. The plate is a
   top-down shot of a fitted tray, held back hard (`brightness .55`) because it is
   GROUND, not content: item icons have to win against it. */
.stash-chest {
  /* The tray plate AND its darkening wash are both background LAYERS here.
     The wash used to be an `inset: 0` pseudo-element, but this element is the
     scroll container, so `inset: 0` only ever covered its first screenful: on a
     short viewport the chest floor ran out mid-list and the remaining slots sat
     on bare page (QA 2026-08-17, screenshot). `local` pins both layers to the
     SCROLLED CONTENT, so the floor is exactly as tall as the list is. */
  background-image:
    linear-gradient(180deg, rgba(8, 14, 24, 0.62), rgba(6, 11, 20, 0.78)),
    var(--stash-plate, none);
  background-size: cover, cover;
  background-position: center, center;
  background-repeat: no-repeat, no-repeat;
  background-attachment: local, local;
  box-shadow: var(--rim-ice-sunken), inset 0 0 60px rgba(4, 8, 14, 0.75);
}
.stash-scroll { position: relative; }
.stash-scroll > * { position: relative; z-index: 1; }
.stash-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-gold) 35%, transparent) transparent;
  /* top room so the hover lift (translateY(-2px)) never clips the first row's border */
  padding: 3px 4px 3px 0;
}
.stash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.65rem;
}
/* Card styling lives in the global .relic component (app.css) — ONE item-card
   grammar across the sea-chest, the Tradestead and every future screen. */
/* The slot tally — a carved chip, not dim fine print. */
.stash-cap {
  display: inline-flex; align-items: baseline;
  padding: 4px 10px;
  border: 1px solid var(--color-border, #2a3140);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(0, 0, 0, 0.18));
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.08);
}

.stash-empty-cell {
  border: 1px dashed var(--color-border, #2a2436);
  border-radius: 0;
  min-height: 108px;
  background: rgba(0,0,0,0.12);
}

</style>
